import type {
  ShareCertificateEntityBasics,
  ShareCertificateSnapshot,
} from '@/features/startTask/api/shareCertificateApi';
import { formatSharesNumeral, sharesToEnglishWords, syncSharesWordFields } from './sharesWords';

export interface ShareCertificateFormModel {
  companyName: string;
  uen: string;
  registeredOfficeAddress: string;
  memberName: string;
  contactCd: string;
  certNo: string;
  shareClass: string;
  noOfShares: string;
  sharesNumeral: string;
  sharesWords: string;
  issueDate: string;
  signersJson: string;
  shareholderIdx?: number | string;
  shareCapitalIdx?: number | string;
}

export function createEmptyShareCertificateForm(): ShareCertificateFormModel {
  return {
    companyName: '',
    uen: '',
    registeredOfficeAddress: '',
    memberName: '',
    contactCd: '',
    certNo: '',
    shareClass: '',
    noOfShares: '',
    sharesNumeral: '',
    sharesWords: '',
    issueDate: '',
    signersJson: '',
  };
}

function trim(v: unknown): string {
  return v === null || v === undefined ? '' : String(v).trim();
}

/** Company block from entity-snapshot or tlinkData (mutates model in place). */
export function applyEntityBasicsToShareCertificateForm(
  model: ShareCertificateFormModel,
  data: ShareCertificateEntityBasics | Record<string, unknown> | null | undefined,
): void {
  if (!data) return;
  const d = data as Record<string, unknown>;
  const name = trim(d.company_name ?? d.companyName ?? d.entityName ?? d.ENTITY_NAME);
  const uen = trim(d.uen ?? d.registrationNo ?? d.regNo ?? d.REG_NO);
  const addr = trim(
    d.registered_office_address ?? d.registeredOfficeAddress ?? d.companyAddress ?? d.address,
  );
  if (name) model.companyName = name;
  if (uen) model.uen = uen;
  if (addr) model.registeredOfficeAddress = addr;
}

export function applySnapshotToShareCertificateForm(
  model: ShareCertificateFormModel,
  snapshot: ShareCertificateSnapshot,
): void {
  model.companyName = trim(snapshot.company_name);
  model.uen = trim(snapshot.uen);
  model.registeredOfficeAddress = trim(snapshot.registered_office_address);
  model.memberName = trim(snapshot.member_name);
  model.contactCd = trim(snapshot.contact_cd);
  model.certNo = trim(snapshot.cert_no);
  model.shareClass = trim(snapshot.share_class);
  model.noOfShares = trim(snapshot.no_of_shares);
  model.issueDate = trim(snapshot.issue_date_iso || snapshot.issue_date);
  model.shareholderIdx = snapshot.shareholder_idx;
  model.shareCapitalIdx = snapshot.share_capital_idx;
  syncSharesWordFields(model);
}

export function toShareCertificateTemplateVariables(
  model: ShareCertificateFormModel,
): Record<string, string> {
  syncSharesWordFields(model);
  const noOfShares = trim(model.noOfShares).replace(/,/g, '');
  const numeral = trim(model.sharesNumeral) || formatSharesNumeral(noOfShares);
  const words = trim(model.sharesWords) || sharesToEnglishWords(Number(noOfShares));

  return {
    company_name: trim(model.companyName),
    uen: trim(model.uen),
    registered_office_address: trim(model.registeredOfficeAddress),
    cert_no: trim(model.certNo),
    no_of_shares: noOfShares,
    shares_numeral: numeral,
    shares_words: words,
    share_class: trim(model.shareClass),
    issue_date: trim(model.issueDate),
    member_name: trim(model.memberName),
    signers: trim(model.signersJson),
    Signers: trim(model.signersJson),
    contact_cd: trim(model.contactCd),
  };
}

export function buildShareCertificateTaskFormData(
  model: ShareCertificateFormModel,
  entityCd: string,
): Record<string, unknown> {
  return {
    ...toShareCertificateTemplateVariables(model),
    entityCd: trim(entityCd),
    entity: trim(entityCd),
  };
}

export function isShareCertificateFormComplete(model: ShareCertificateFormModel): boolean {
  // signersJson is built at Create Task (directors + secretaries) — not part of snapshot readiness
  return [
    model.companyName,
    model.uen,
    model.registeredOfficeAddress,
    model.memberName,
    model.contactCd,
    model.certNo,
    model.shareClass,
    model.noOfShares,
    model.issueDate,
  ].every((v) => trim(v).length > 0);
}
